import React, { useState, useEffect } from 'react';
import './FourOpinionsExercise.css';

const FourOpinionsExercise = ({ opinions = [], title = '', level = 'B1', topic = '', onClose, updateAttributes, node: _node, editor: _editor, getPos: _getPos, isStudent = false, myState: _myState = {}, otherState, updateState }) => {
  otherState = otherState || {};
  updateState = updateState || (() => {});
  const [isEditing, setIsEditing] = useState(false);
  const [editableOpinions, setEditableOpinions] = useState([...opinions]);
  const [editableTitle, setEditableTitle] = useState('Four Opinions');
  const [editableLevel, setEditableLevel] = useState(level);
  // eslint-disable-next-line no-unused-vars
  const [editableTopic, setEditableTopic] = useState(topic);
  const [isDiscussionExpanded, setIsDiscussionExpanded] = useState(false);
  const [isInstructionsExpanded, setIsInstructionsExpanded] = useState(false);

  useEffect(() => {
    if (otherState.discussionExpanded !== undefined && otherState.discussionExpanded !== isDiscussionExpanded) {
      setIsDiscussionExpanded(otherState.discussionExpanded);
    }
  }, [otherState.discussionExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = () => setIsEditing(true);
  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        opinions: editableOpinions,
        title: editableTitle,
        level: editableLevel,
        topic: editableTitle, // topic теперь равен title
      });
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
    setEditableOpinions([...opinions]);
    setEditableTitle('Four Opinions');
    setEditableLevel(level);
    setEditableTopic(topic);
  };

  const updateOpinion = (index, field, value) => {
    setEditableOpinions(prev =>
      prev.map((opinion, i) =>
        i === index ? { ...opinion, [field]: value } : opinion
      )
    );
  };


  // Level-specific adaptations
  const getLevelConfig = (level) => {
    const configs = {
      'A1': {
        instruction: 'Read the opinions and discuss with your partner. Use simple words.',
        complexity: 'basic',
        showHints: true,
        hintText: 'Use words like: I think, I like, I don\'t like',
        colorScheme: '#4CAF50',
        difficulty: 'Beginner'
      },
      'A2': {
        instruction: 'Read the opinions and discuss. Try to explain why you agree or disagree.',
        complexity: 'elementary',
        showHints: true,
        hintText: 'Use phrases like: I agree because..., I disagree because...',
        colorScheme: '#2196F3',
        difficulty: 'Elementary'
      },
      'B1': {
        instruction: 'Read the opinions and have a discussion. Express your own viewpoint clearly.',
        complexity: 'intermediate',
        showHints: true,
        hintText: 'Use phrases like: I believe that..., In my opinion..., I\'m convinced that..., I tend to think...',
        colorScheme: '#FF9800',
        difficulty: 'Intermediate'
      },
      'B2': {
        instruction: 'Analyze the opinions critically and engage in a detailed discussion.',
        complexity: 'upper-intermediate',
        showHints: true,
        hintText: 'Use phrases like: From my perspective..., I would argue that..., It seems to me that..., I\'m inclined to believe...',
        colorScheme: '#9C27B0',
        difficulty: 'Upper Intermediate'
      },
      'C1': {
        instruction: 'Evaluate the opinions from multiple perspectives and engage in sophisticated debate.',
        complexity: 'advanced',
        showHints: true,
        hintText: 'Use phrases like: I would contend that..., From a critical standpoint..., It could be argued that..., I maintain that...',
        colorScheme: '#F44336',
        difficulty: 'Advanced'
      },
      'C2': {
        instruction: 'Critically examine the opinions and engage in nuanced, academic-level discussion.',
        complexity: 'mastery',
        showHints: true,
        hintText: 'Use phrases like: I would posit that..., From an analytical perspective..., It is my contention that..., I would assert that...',
        colorScheme: '#795548',
        difficulty: 'Mastery'
      }
    };
    return configs[level] || configs['B1'];
  };

  const levelConfig = getLevelConfig(editableLevel);

  // Function to highlight useful phrases in opinion text based on level
  const highlightPhrases = (text) => {
    if (!text) return text;

    // Level-specific useful phrases and words
    const levelPhrases = {
      'A1': [
        'I think', 'I like', 'I don\'t like', 'I agree', 'I disagree',
        'Yes', 'No', 'Maybe', 'Good', 'Bad', 'Nice', 'Great',
        'because', 'and', 'but', 'so', 'very', 'really', 'too'
      ],
      'A2': [
        'I think', 'I believe', 'In my opinion', 'I feel', 'I consider',
        'I agree', 'I disagree', 'I support', 'I oppose', 'I prefer',
        'because', 'so', 'but', 'however', 'although', 'while',
        'first', 'second', 'finally', 'also', 'too', 'as well',
        'I think that', 'I believe that', 'I feel that'
      ],
      'B1': [
        'I believe', 'In my opinion', 'I feel', 'I consider', 'I think',
        'I agree', 'I disagree', 'I support', 'I oppose', 'I favor',
        'I prefer', 'I would argue', 'I maintain', 'I contend',
        'From my perspective', 'It seems to me', 'I\'m convinced',
        'I tend to think', 'I\'m inclined to believe', 'I would say',
        'because', 'therefore', 'however', 'although', 'while',
        'furthermore', 'moreover', 'additionally', 'on the other hand',
        'I believe that', 'I think that', 'I feel that', 'I consider that'
      ],
      'B2': [
        'I would argue', 'I maintain', 'I contend', 'I would suggest',
        'I would recommend', 'I would propose', 'I would say',
        'From my perspective', 'It seems to me', 'I\'m convinced',
        'I tend to think', 'I\'m inclined to believe',
        'I would argue that', 'I believe that', 'I think that',
        'I feel that', 'I consider that', 'I maintain that',
        'furthermore', 'moreover', 'additionally', 'consequently',
        'nevertheless', 'nonetheless', 'on the contrary', 'in contrast',
        'it could be argued', 'it might be suggested', 'one could say'
      ],
      'C1': [
        'I would contend', 'I would assert', 'I would posit',
        'I would argue that', 'I believe that', 'I think that',
        'I feel that', 'I consider that', 'I maintain that',
        'From a critical standpoint', 'It could be argued that',
        'I would contend that', 'I would assert that', 'I would posit that',
        'furthermore', 'moreover', 'additionally', 'consequently',
        'nevertheless', 'nonetheless', 'on the contrary', 'in contrast',
        'it could be argued', 'it might be suggested', 'one could say',
        'it is my contention', 'I would maintain', 'I would suggest',
        'from an analytical perspective', 'it is evident that'
      ],
      'C2': [
        'I would posit', 'I would assert', 'I would contend',
        'I would argue that', 'I believe that', 'I think that',
        'I feel that', 'I consider that', 'I maintain that',
        'From an analytical perspective', 'It is my contention that',
        'I would posit that', 'I would assert that', 'I would contend that',
        'furthermore', 'moreover', 'additionally', 'consequently',
        'nevertheless', 'nonetheless', 'on the contrary', 'in contrast',
        'it could be argued', 'it might be suggested', 'one could say',
        'it is my contention', 'I would maintain', 'I would suggest',
        'from an analytical perspective', 'it is evident that',
        'it is imperative that', 'it is crucial that', 'it is essential that',
        'I would postulate', 'I would hypothesize', 'I would theorize'
      ]
    };

    const phrases = levelPhrases[editableLevel] || levelPhrases['B1'];

    let highlightedText = text;
    phrases.forEach(phrase => {
      const regex = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      highlightedText = highlightedText.replace(regex, (match, offset, string) => {
        // Проверяем, стоит ли фраза в начале предложения
        const isStartOfSentence = offset === 0 || /[.!?]\s*$/.test(string.substring(0, offset));
        // Если да, делаем первую букву заглавной
        const capitalizedPhrase = isStartOfSentence && match[0] === match[0].toLowerCase()
          ? match[0].toUpperCase() + match.slice(1)
          : match;
        return `<strong>${capitalizedPhrase}</strong>`;
      });
    });

    // Также исправляем первую букву всего текста, если она маленькая
    if (highlightedText.length > 0 && highlightedText[0] === highlightedText[0].toLowerCase()) {
      // Проверяем, не является ли первый символ частью HTML тега
      if (!highlightedText.startsWith('<')) {
        highlightedText = highlightedText[0].toUpperCase() + highlightedText.slice(1);
      } else {
        // Если начинается с HTML тега, ищем первый текстовый символ после тега
        const textMatch = highlightedText.match(/^<[^>]*>([a-z])/);
        if (textMatch) {
          const tagEnd = highlightedText.indexOf('>') + 1;
          highlightedText = highlightedText.substring(0, tagEnd) +
                           highlightedText[tagEnd].toUpperCase() +
                           highlightedText.substring(tagEnd + 1);
        }
      }
    }

    return highlightedText;
  };

  // Level-specific styling
  const getLevelStyle = () => ({
    '--level-color': levelConfig.colorScheme,
    '--level-opacity': levelConfig.complexity === 'basic' ? '0.8' : '1',
    '--text-complexity': levelConfig.complexity === 'basic' ? '1.1' : '1'
  });

  return (
    <div className="fo-exercise" style={getLevelStyle()}>
      <div className="fo-header">
        <div className="fo-header-left">
          <div className="fo-header-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={editableTitle}
              onChange={(e) => setEditableTitle(e.target.value)}
              className="fo-title-input"
            />
          ) : (
            <span className="fo-title">Four Opinions</span>
          )}
          {isEditing ? (
            <input
              type="text"
              value={editableLevel}
              onChange={(e) => setEditableLevel(e.target.value)}
              className="fo-level-input"
              placeholder="Level"
            />
          ) : (
            <span className="fo-level-badge">{editableLevel}</span>
          )}
        </div>
        <div className="fo-header-actions">
          {!isEditing && !isStudent && (
            <button className="fo-icon-btn" aria-label="Edit" data-tip="Edit" onClick={handleEdit}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {!isEditing && onClose && (
            <button className="fo-icon-btn" aria-label="Close" data-tip="Close" onClick={onClose}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="fo-body">
        {/* How to use — teacher only */}
        {!isEditing && !isStudent && (
          <div className="fo-collapsible">
            <div
              className="fo-collapsible-header"
              onClick={() => setIsInstructionsExpanded(!isInstructionsExpanded)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setIsInstructionsExpanded(!isInstructionsExpanded)}
            >
              <span className="fo-collapsible-title">How to use this exercise</span>
              <svg
                className={`fo-collapsible-arrow${isInstructionsExpanded ? ' is-open' : ''}`}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              >
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </div>
            {isInstructionsExpanded && (
              <div className="fo-collapsible-content">
                <ul className="fo-instructions-list">
                  <li>Read the different opinions on the topic aloud or silently</li>
                  <li>Ask students which opinion they agree or disagree with and why</li>
                  <li>Encourage use of the helpful phrases shown at the top</li>
                  <li>Use the discussion questions to extend the conversation</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Level-specific hints for A1 and A2 */}
        {levelConfig.showHints && (
          <div className="fo-hint-box" style={{ background: levelConfig.colorScheme + '10', borderColor: levelConfig.colorScheme + '30' }}>
            <div className="fo-hint-header">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="0.8" fill="currentColor" />
              </svg>
              <span>Helpful phrases</span>
            </div>
            <p className="fo-hint-text">{levelConfig.hintText}</p>
          </div>
        )}

        <div className="fo-opinions-grid">
          {editableOpinions.map((opinion, index) => (
            <div key={index} className={`fo-opinion-card fo-opinion-${index % 4}`}>
              <div className="fo-opinion-header">
                {isEditing ? (
                  <input
                    type="text"
                    value={opinion.name || ''}
                    onChange={(e) => updateOpinion(index, 'name', e.target.value)}
                    className="fo-name-input"
                    placeholder="Name"
                  />
                ) : (
                  <h4 className="fo-opinion-name">{opinion.name || `Person ${index + 1}`}</h4>
                )}
              </div>
              <div className="fo-opinion-content">
                {isEditing ? (
                  <div>
                    <div className="fo-richtext-toolbar">
                      <button
                        type="button"
                        onClick={() => document.execCommand('bold', false, null)}
                        className="fo-richtext-btn"
                        style={{ fontWeight: 700 }}
                        title="Bold"
                      >
                        B
                      </button>
                      <button
                        type="button"
                        onClick={() => document.execCommand('italic', false, null)}
                        className="fo-richtext-btn"
                        style={{ fontStyle: 'italic' }}
                        title="Italic"
                      >
                        I
                      </button>
                      <button
                        type="button"
                        onClick={() => document.execCommand('underline', false, null)}
                        className="fo-richtext-btn"
                        style={{ textDecoration: 'underline' }}
                        title="Underline"
                      >
                        U
                      </button>
                    </div>

                    <div
                      className="fo-opinion-input"
                      contentEditable={true}
                      dangerouslySetInnerHTML={{ __html: opinion.opinion || '' }}
                      onInput={(e) => {
                        let html = e.target.innerHTML;
                        // Исправляем первую букву предложения, если она маленькая
                        // Проверяем, не является ли первый символ частью HTML тега
                        if (html.length > 0 && !html.startsWith('<')) {
                          // Если текст начинается не с HTML тега, проверяем первую букву
                          const firstChar = html[0];
                          if (firstChar === firstChar.toLowerCase() && /[a-z]/.test(firstChar)) {
                            html = firstChar.toUpperCase() + html.slice(1);
                            e.target.innerHTML = html;
                          }
                        } else if (html.startsWith('<')) {
                          // Если начинается с HTML тега, ищем первый текстовый символ
                          const textMatch = html.match(/^<[^>]*>([a-z])/);
                          if (textMatch) {
                            const tagEnd = html.indexOf('>') + 1;
                            html = html.substring(0, tagEnd) +
                                   html[tagEnd].toUpperCase() +
                                   html.substring(tagEnd + 1);
                            e.target.innerHTML = html;
                          }
                        }
                        updateOpinion(index, 'opinion', html);
                      }}
                      onBlur={(e) => {
                        // Исправляем первую букву при потере фокуса
                        let html = e.target.innerHTML;
                        if (html.length > 0 && !html.startsWith('<')) {
                          const firstChar = html[0];
                          if (firstChar === firstChar.toLowerCase() && /[a-z]/.test(firstChar)) {
                            html = firstChar.toUpperCase() + html.slice(1);
                            e.target.innerHTML = html;
                            updateOpinion(index, 'opinion', html);
                          }
                        } else if (html.startsWith('<')) {
                          const textMatch = html.match(/^<[^>]*>([a-z])/);
                          if (textMatch) {
                            const tagEnd = html.indexOf('>') + 1;
                            html = html.substring(0, tagEnd) +
                                   html[tagEnd].toUpperCase() +
                                   html.substring(tagEnd + 1);
                            e.target.innerHTML = html;
                            updateOpinion(index, 'opinion', html);
                          }
                        }
                      }}
                    />
                  </div>
                ) : (
                  <p
                    className="fo-opinion-text"
                    dangerouslySetInnerHTML={{
                      __html: highlightPhrases(opinion.opinion || 'No opinion provided')
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Level-specific discussion prompts */}
        <div className="fo-collapsible fo-discussion">
          <div
            className="fo-collapsible-header"
            onClick={() => { const next = !isDiscussionExpanded; setIsDiscussionExpanded(next); updateState({ discussionExpanded: next }); }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setIsDiscussionExpanded(!isDiscussionExpanded)}
          >
            <span className="fo-collapsible-title">Discussion Questions</span>
            <svg
              className={`fo-collapsible-arrow${isDiscussionExpanded ? ' is-open' : ''}`}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            >
              <polyline points="9,18 15,12 9,6"></polyline>
            </svg>
          </div>
          {isDiscussionExpanded && (
            <div className="fo-collapsible-content">
              <ul className="fo-prompts-list">
                {editableLevel === 'A1' && (
                  <>
                    <li>Which opinion do you like most? Why?</li>
                    <li>Do you agree with any of these opinions?</li>
                    <li>What do you think about this topic?</li>
                  </>
                )}
                {editableLevel === 'A2' && (
                  <>
                    <li>Which opinion do you agree with? Explain why.</li>
                    <li>Do you disagree with any opinion? Why?</li>
                    <li>What is your own opinion on this topic?</li>
                  </>
                )}
                {editableLevel === 'B1' && (
                  <>
                    <li>Which opinion do you find most convincing? Why?</li>
                    <li>What are the strengths and weaknesses of each opinion?</li>
                    <li>How would you respond to each of these viewpoints?</li>
                  </>
                )}
                {editableLevel === 'B2' && (
                  <>
                    <li>Analyze the underlying assumptions in each opinion.</li>
                    <li>Which opinion presents the most balanced perspective?</li>
                    <li>How do these opinions reflect different cultural or social perspectives?</li>
                  </>
                )}
                {editableLevel === 'C1' && (
                  <>
                    <li>Evaluate the logical coherence and evidence presented in each opinion.</li>
                    <li>How do these opinions relate to broader societal debates?</li>
                    <li>What are the implications of each viewpoint for policy or practice?</li>
                  </>
                )}
                {editableLevel === 'C2' && (
                  <>
                    <li>Critically examine the philosophical and ethical foundations of each opinion.</li>
                    <li>How do these opinions contribute to or challenge existing theoretical frameworks?</li>
                    <li>What are the long-term consequences and systemic implications of each perspective?</li>
                  </>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="fo-footer">
          <button className="fo-cancel-btn" onClick={handleCancel}>Cancel</button>
          <button className="fo-save-btn" onClick={handleSave}>Save</button>
        </div>
      )}
    </div>
  );
};

export default FourOpinionsExercise;
